/** Display helpers. Money arrives from the API as integer paisa, always. */

import type { Locale } from './i18n';
import { bsDateLabel, bsDayMonthLabel, clockTime, toBs } from './bs-date';

/**
 * The app's language, for the date formatters below.
 *
 * Module state rather than a parameter, because there are some thirty
 * callers, every one inside a component that already re-renders on a language
 * change — and `labels.map(dayMonth)` would hand an optional second parameter
 * the array index. `LocaleProvider` sets it while rendering, before any child
 * formats a date, so the first paint is already in the right language.
 */
let formatLocale: Locale = 'en';
export const setFormatLocale = (locale: Locale): void => { formatLocale = locale; };

export function npr(paisa: string | number): string {
  // Rounded on the way in. Paisa are integers by contract, but a caller that
  // multiplies by a VAT rate in floating point arrives with 90399.99999999999,
  // and `abs % 100` on that renders as "903.99.99999999998545". Rounding here
  // means a caller's slip shows up as a rounding difference rather than as
  // garbage in front of a customer. Use withVat() to get the arithmetic right.
  const n = Math.round(Number(paisa));
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const whole = Math.floor(abs / 100).toString();
  const cents = String(abs % 100).padStart(2, '0');
  // Nepali grouping: last three digits, then pairs.
  const head = whole.length > 3 ? whole.slice(0, -3) : '';
  const tail = whole.slice(-3);
  const grouped = head ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}` : tail;
  return `${sign}NPR ${grouped}.${cents}`;
}

/**
 * The price a parent pays, VAT included — rounded to the WHOLE RUPEE, as the
 * server prices every charge (`priceDocument` in api/src/lib/money.ts), so a
 * page never shows NPR 499.99 for something billed at NPR 500.00. At 0% VAT
 * nothing is rounded.
 */
export const withVat = (pricePaisa: string | number, vatRateBp: number): number => {
  const net = Number(pricePaisa);
  if (!vatRateBp || net <= 0) return Math.max(0, net);
  return Math.round((net + (net * vatRateBp) / 10000) / 100) * 100;
};

/*
 * EVERY DATE IS BIKRAM SAMBAT — "1 Mangsir 2083", or "1 मंसिर 2083" in
 * Nepali. A date outside the conversion table (BS 1970–2090) falls back to the
 * Gregorian form these functions used to print, rather than to nothing.
 */

/** "1 Mangsir 2083". */
export const date = (value: string | Date): string => {
  const when = new Date(value);
  const day = toBs(when);
  return day
    ? bsDateLabel(day, formatLocale)
    : when.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * "1 Mangsir" — a date short enough to sit under a chart's tick.
 *
 * The trend chart was labelling its axis with the raw `YYYY-MM-DD` the model
 * carries, so a parent read "2026-08-22" under a point. That is a key, not a
 * date; it is 10 characters where 6 will do, and the year is the same on every
 * tick of a chart that spans a term.
 *
 * A bare `YYYY-MM-DD` is parsed by `Date` as UTC midnight, which in Nepal
 * (UTC+5:45) is still the same calendar day — but west of Greenwich it is the
 * previous evening, and the tick would name the day before the paper. Splitting
 * the string and building a LOCAL date keeps the label on the day the model
 * says, wherever it is read. A `Date` is taken as it is.
 */
export const dayMonth = (value: string | Date): string => {
  const iso = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  const when = iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : new Date(value);
  if (Number.isNaN(when.getTime())) return String(value);
  const day = toBs(when);
  return day
    ? bsDayMonthLabel(day, formatLocale)
    : when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

/** "1 Mangsir, 03:45 PM" — no year, as before. */
export const dateTime = (value: string | Date): string => {
  const when = new Date(value);
  const day = toBs(when);
  return day
    ? `${bsDayMonthLabel(day, formatLocale)}, ${clockTime(when, formatLocale)}`
    : when.toLocaleString(undefined, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
};

export const clock = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

export function stateTone(state: string): 'ok' | 'warn' | 'bad' | 'neutral' {
  if (['active', 'trial', 'grace'].includes(state)) return 'ok';
  if (['past_due', 'paused'].includes(state)) return 'warn';
  if (['suspended', 'cancelled', 'expired'].includes(state)) return 'bad';
  return 'neutral';
}
